/**
 * intelligenceVoice.ts — typed wire contracts for the Phibian Intelligence
 * voice/transcript domain, aligned FIELD-FOR-FIELD with the authoritative
 * server shapes in `apps/api/src/domains/intelligence/voice/*.ts` (mission
 * lane M2, merged). The server is the source of truth; this module only
 * re-declares its shapes so drift between the two is loud, never silent.
 *
 * Sources of truth:
 *   - TranscriptRecord → `voice/types.ts` + `voice/transcript.ts`
 *   - AudioUploadToken → `voice/types.ts` + `voice/audio.ts`
 *   - VoiceDraft / VoiceDraftVersion / version chain → `voice/types.ts` +
 *     `voice/drafts.ts`
 *
 * The hard rules these contracts encode, exactly as the server does:
 *
 *   - TRANSCRIPTS ARE PHI: the record carries `sensitivityClassification`
 *     (required on the wire — the 'restricted' DEFAULT is applied
 *     server-side at construction, never client-side) and `retentionClass`
 *     (SHORT = the bounded default TTL).
 *
 *   - THE RECORD NEVER CARRIES TEXT: the immutable TranscriptRecord binds
 *     the transcript text by `contentHash` (64 lowercase hex) only. The
 *     editable text lives in VoiceDraft versions, never on the record.
 *
 *   - RAW AUDIO IS NEVER A FIELD: no audio-bytes field exists in any of
 *     these shapes. The only audio reference is the opaque, TTL-bound
 *     AudioUploadToken (ids + window, never bytes). Every schema here is
 *     `.strict()` — an unknown key is rejected — so there is structurally
 *     no field in which audio bytes, transcript text, or
 *     grant/scope/permission fields can ride along. The server-side
 *     deep-walk guard (`assertNoRawAudioFields`) is deliberately NOT
 *     duplicated here (see footer + lane report).
 *
 *   - EDITS ARE VERSIONED, NOT REWRITES: a draft is the head of an
 *     append-only version chain; v1 is machine-created (no editor), every
 *     later version records `editedByUserId` + `editedAt`, and `final` is
 *     terminal. The client pins the chain SHAPE; hash recomputation and
 *     store semantics are server-side.
 *
 * Client-side authority-fence discipline: grant/scope/permission fields are
 * rejected client-side by the strict schemas; semantic validation (content
 * hash recomputation, the raw-audio deep walk, retention-expiry math, store
 * integrity) stays server-side.
 */
import { z } from 'zod';
import { sensitivityClassificationSchema } from './intelligencePersonalization.js';
// ---------------------------------------------------------------------------
// Id shapes — mirrors voice/types.ts
// ---------------------------------------------------------------------------
export const TRANSCRIPT_ID_PREFIX = 'trs_';
export const AUDIO_TOKEN_ID_PREFIX = 'aup_';
export const DRAFT_ID_PREFIX = 'vdr_';
export const SHA256_HEX_LENGTH = 64;
/** sha256 hex, exactly 64 chars, lowercase — the server `isSha256Hex` shape. */
const HEX64 = new RegExp(`^[0-9a-f]{${SHA256_HEX_LENGTH}}$`);
/** True when `value` is a 64-character lowercase hex sha256 (server parity). */
export function isSha256Hex(value) {
    return typeof value === 'string' && HEX64.test(value);
}
/** True when `value` looks like a transcript id ('trs_' + 64 lowercase hex). */
export function isTranscriptId(value) {
    return (typeof value === 'string' &&
        value.startsWith(TRANSCRIPT_ID_PREFIX) &&
        HEX64.test(value.slice(TRANSCRIPT_ID_PREFIX.length)));
}
/** True when `value` looks like an opaque audio upload token id ('aup_' + 64 hex). */
export function isAudioTokenId(value) {
    return (typeof value === 'string' &&
        value.startsWith(AUDIO_TOKEN_ID_PREFIX) &&
        HEX64.test(value.slice(AUDIO_TOKEN_ID_PREFIX.length)));
}
/** True when `value` looks like a draft id ('vdr_' + 64 lowercase hex). */
export function isDraftId(value) {
    return (typeof value === 'string' &&
        value.startsWith(DRAFT_ID_PREFIX) &&
        HEX64.test(value.slice(DRAFT_ID_PREFIX.length)));
}
export const VOICE_RETENTION_CLASSES = ['SHORT', 'STANDARD'];
export const voiceRetentionClassSchema = z.enum(['SHORT', 'STANDARD']);
// ---------------------------------------------------------------------------
// TranscriptRecord — mirrors voice/types.ts TranscriptRecord
// ---------------------------------------------------------------------------
/**
 * The immutable voice transcript record, field-for-field the server's
 * TranscriptRecord. The record binds the transcript text by `contentHash`
 * only — it never carries the text (PHI stays behind the trace boundary).
 * Every field below is the server's field, with the server's shape and NO
 * client-side defaults: the defaults (sourceType 'voice', sensitivity
 * 'restricted', retention SHORT, provenanceRefs []) are applied server-side
 * at construction (`makeTranscriptRecord`); the client validates the wire
 * record, never constructs one.
 */
export const voiceTranscriptSchema = z
    .object({
    /** Derived, deterministic ('trs_' + sha256 of the canonical envelope). */
    transcriptId: z.string().refine(isTranscriptId, {
        message: 'transcriptId must match trs_<64-hex>',
    }),
    /** Tenant scoping — every retrieval path inherits the caller's scoping. */
    tenantOrgId: z.string().min(1),
    uploaderUserId: z.string().min(1),
    /** Epoch ms, injected by the caller (no clocks client-side). */
    createdAt: z.number().finite().nonnegative(),
    /** A transcript in this contract is always a voice transcript. */
    sourceType: z.literal('voice'),
    /** Optional recording metadata. */
    durationMs: z.number().finite().nonnegative().optional(),
    languageCode: z.string().min(1).optional(),
    /** PHI-capable by construction; the wire record always carries one. */
    sensitivityClassification: sensitivityClassificationSchema,
    /** sha256 (lowercase hex) of the transcript text. The text is NOT a field. */
    contentHash: z.string().refine(isSha256Hex, {
        message: 'contentHash must be a 64-character lowercase hex sha256',
    }),
    /** SHORT is the bounded default TTL (applied server-side). */
    retentionClass: voiceRetentionClassSchema,
    /** Evidence-manifest refs backing the transcription. Refs only, never names. */
    provenanceRefs: z.array(z.string().min(1)),
})
    .strict();
// ---------------------------------------------------------------------------
// AudioUploadToken — the ONLY audio reference (mirrors voice/audio.ts)
// ---------------------------------------------------------------------------
/**
 * The raw-audio non-retention rule is structural: the contract has no
 * audio-bytes field of any kind; the only audio reference is this opaque,
 * TTL-bound upload token — ids, a binding to the transcript, and a window.
 * A token type, never bytes. `.strict()`: no field exists in which samples,
 * buffers or blobs could ride along.
 */
export const audioUploadTokenSchema = z
    .object({
    kind: z.literal('audioUploadToken'),
    /** Opaque, derived ('aup_' + sha256). Never derivable back to audio content. */
    tokenId: z.string().min(1),
    /** The transcript this upload binds to (server parity: opaque non-empty). */
    transcriptId: z.string().min(1),
    uploadedByUserId: z.string().min(1),
    createdAt: z.number().finite().nonnegative(),
    /** TTL bound: after this epoch-ms the reference is expired. */
    expiresAt: z.number().finite(),
})
    .strict()
    .superRefine((token, ctx) => {
    if (token.expiresAt <= token.createdAt) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'expiresAt must be a finite number strictly after createdAt',
        });
    }
});
// ---------------------------------------------------------------------------
// VoiceDraft + version chain — mirrors voice/drafts.ts
// ---------------------------------------------------------------------------
/**
 * The provenance rule (server `validateVoiceDraftEnvelope`, pinned here
 * structurally): v1 is the machine-created copy of the transcript text and
 * carries NO editedByUserId; every later version records a non-empty
 * editedByUserId.
 */
function draftProvenanceRule(draft, ctx) {
    if (draft.version === 1) {
        if (draft.editedByUserId !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'version 1 is machine-created from the transcript and must carry no editedByUserId',
            });
        }
    }
    else if (draft.editedByUserId === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'every user edit (version > 1) must record a non-empty editedByUserId',
        });
    }
}
/** The version-entry fields: the draft shape minus `audioId` (server parity). */
const draftVersionFields = {
    /** Derived ('vdr_' + sha256), one draft per transcript+audio pair. */
    draftId: z.string().refine(isDraftId, { message: 'draftId must match vdr_<64-hex>' }),
    /** The immutable original this draft edits on top of. */
    transcriptId: z.string().refine(isTranscriptId, {
        message: 'transcriptId must match trs_<64-hex>',
    }),
    /** 1 = machine copy of the transcript; every edit appends a new version. */
    version: z.number().int().min(1),
    /** The current editable text. */
    text: z.string(),
    /** Absent on v1; REQUIRED on every later version (provenance). */
    editedByUserId: z.string().min(1).optional(),
    /** Epoch ms of the last edit. */
    editedAt: z.number().finite().nonnegative(),
    /** Terminal flag: once set, further edits are rejected server-side. */
    final: z.boolean(),
    /** sha256 (lowercase hex) of `text` — shape pinned; recomputation is server-side. */
    contentHash: z.string().refine(isSha256Hex, {
        message: 'contentHash must be a 64-character lowercase hex sha256',
    }),
};
/**
 * The editable-composer seam: transcript → editable draft → edits with
 * provenance → finalize. The draft is the HEAD of an append-only version
 * chain; the original transcript is never rewritten.
 */
export const voiceDraftSchema = z
    .object({ ...draftVersionFields, audioId: z.string().min(1) })
    .strict()
    .superRefine(draftProvenanceRule);
/**
 * One immutable entry in a draft's version chain — the draft shape minus
 * `audioId` (server VERSION_FIELD_ALLOWLIST = DRAFT_FIELD_ALLOWLIST −
 * audioId). v1 has no editor; later versions record editor + timestamp.
 */
export const voiceDraftVersionSchema = z
    .object(draftVersionFields)
    .strict()
    .superRefine(draftProvenanceRule);
/**
 * The version-chain pin. Mirrors the STRUCTURAL checks of the server's
 * `validateVersionChain`: a non-empty chain starting at v1, versions exactly
 * 1..n, one shared draftId/transcriptId, non-decreasing editedAt, and
 * `final` only ever on the last version. Per-entry contentHash
 * RECOMPUTATION (sha256(text)) and the raw-audio deep walk remain
 * server-side semantic validation.
 */
export const voiceVersionChainSchema = z
    .array(voiceDraftVersionSchema)
    .min(1, 'a version chain must contain at least the machine-created v1')
    .superRefine((versions, ctx) => {
    const first = versions[0];
    let lastEditedAt = -Infinity;
    let sawFinal = false;
    versions.forEach((entry, index) => {
        if (entry.version !== index + 1) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `versions[${index}]: expected version ${index + 1}, got ${entry.version}`,
            });
        }
        if (first !== undefined && index > 0) {
            if (entry.draftId !== first.draftId) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `versions[${index}]: draftId does not match versions[0]`,
                });
            }
            if (entry.transcriptId !== first.transcriptId) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `versions[${index}]: transcriptId does not match versions[0]`,
                });
            }
        }
        if (entry.editedAt < lastEditedAt) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `versions[${index}]: editedAt precedes an earlier version`,
            });
        }
        lastEditedAt = entry.editedAt;
        if (entry.final) {
            if (sawFinal) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `versions[${index}]: more than one final version`,
                });
            }
            sawFinal = true;
        }
        else if (sawFinal) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `versions[${index}]: a version follows the final version`,
            });
        }
    });
});
// ---------------------------------------------------------------------------
// Deliberate NON-mirrors (server-side semantics the client does not repeat)
// ---------------------------------------------------------------------------
//
//  - contentHash RECOMPUTATION (sha256(text) === contentHash): the server
//    recomputes at envelope + chain validation; the client pins the 64-hex
//    SHAPE only. Recomputing client-side would duplicate semantic validation
//    and require a sha256 implementation in the api-client package.
//  - `assertNoRawAudioFields` (the deep-walk denylist): a server-side
//    structural security guard. The client's strict allowlists already make
//    it impossible for an audio-bytes-shaped key to survive a parse; a
//    second copy of the guard would be a second security boundary to drift.
//  - Retention TTL tables and expiry math (`VOICE_RETENTION_TTL_MS`,
//    `voiceRetentionExpiresAt`, `isVoiceRetentionExpired`) and the
//    TTL-expiry check on tokens: server-side clock policy; the client pins
//    only the window SHAPE (`expiresAt` strictly after `createdAt`).
//  - Construction + store semantics (`makeTranscriptRecord`,
//    `makeAudioUploadToken`, `InMemoryVoiceDraftStore`, `deepFreeze`,
//    `verifyStoreIntegrity`): server behavior. The client validates wire
//    records, never constructs or stores them, so the input-with-defaults
//    types (`TranscriptRecordInput`, `AudioUploadTokenInput`) and the store
//    are deliberately not mirrored.
//# sourceMappingURL=intelligenceVoice.js.map